import { useState } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ParishCombobox } from '@/components/comboboxes/ParishCombobox/ParishCombobox';
import { ProductsLinks } from '@/components/ProductsLinks/ProductsLinks';
import { TextTruncate } from '@/components/TextTruncate/TextTruncate';
import { ActionIcon, Avatar, Menu } from '@mantine/core';
import { IconGridDots, IconLogout } from '@tabler/icons-react';
import { useAuth, useCurrentUserProfile, useImpersonation, useRedirectToPublicProfile } from '@/hooks';
import { useTranslation } from 'react-i18next';
import styles from './UserMenu.module.sass';

export const UserMenu = () => {
  const { logout } = useAuth();
  const { data: userProfile } = useCurrentUserProfile();
  const { impersonatedUserEmail, impersonatedUserName, isOverallAdmin, isImpersonating } = useImpersonation();
  const { redirectToPublicProfilePage, isRedirectPending } = useRedirectToPublicProfile();
  const { t } = useTranslation('global');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const defaultDisplayName = `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim();
  const displayName = isImpersonating
    ? impersonatedUserName || impersonatedUserEmail || defaultDisplayName
    : defaultDisplayName;
  const displayAvatarSource = isImpersonating ? null : userProfile?.profileImageUrl;

  const handleViewProfileClick = () => {
    setIsMenuOpen(false);
    redirectToPublicProfilePage({ returnToMpaUrl: window.location.href });
  };

  return (
    <ErrorBoundary>
      <Menu withArrow withinPortal={true} opened={isMenuOpen} onChange={setIsMenuOpen}>
        <Menu.Target>
          <ActionIcon size="xl" radius="100%" variant="transparent" color="dark">
            <IconGridDots size={20} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown className={styles.menuDropdown}>
          <section className={styles.userInfo}>
            <div className={styles.userInfoContent}>
              <div>
                {userProfile && (
                  <Avatar
                    size="xl"
                    classNames={{ placeholder: styles.avatarPlaceholder }}
                    name={displayName}
                    src={displayAvatarSource}
                    radius="100%"
                    color="brand.9"
                  />
                )}
              </div>
              <div>
                <TextTruncate className={styles.userName} text={displayName} />
                {!isOverallAdmin ? (
                  <Menu.Item
                    className={styles.viewProfileLink}
                    onClick={handleViewProfileClick}
                    disabled={isRedirectPending}
                  >
                    {t('mainHeader.userMenu.viewProfile')}
                  </Menu.Item>
                ) : null}
              </div>
            </div>

            <ActionIcon
              size="xl"
              radius="100%"
              variant="light"
              color="base"
              c="base.6"
              bg="transparent"
              className={styles.logoutButton}
              onClick={logout}
            >
              <IconLogout size={20} stroke={1.5} />
            </ActionIcon>
          </section>

          {userProfile && !isImpersonating ? (
            <ParishCombobox className={styles.parishPicker} onChurchChange={() => setIsMenuOpen(false)} />
          ) : null}

          <ProductsLinks onProductSelect={() => setIsMenuOpen(false)} />
        </Menu.Dropdown>
      </Menu>
    </ErrorBoundary>
  );
};
